import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { 
  Key, ArrowLeft, Download, Calendar, 
  Home, MapPin, TrendingUp, AlertCircle, Clock
} from "lucide-react";
import { API } from "@/App";

const TenUResLogo = ({ size = "text-4xl" }) => (
  <span className={`tenures-logo ${size}`}>
    Ten<span className="tenures-ur font-extrabold">UR</span>es
  </span>
);

export default function UnlockModule({ user }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    fetchCalculation();
  }, []);

  const fetchCalculation = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/unlock/calculate`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        withCredentials: true
      });
      setResult(response.data);
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to calculate";
      if (message.includes("onboarding")) {
        toast.error("Please complete onboarding first");
        navigate('/onboarding');
      }
    } finally {
      setLoading(false);
    }
  };

  const downloadPDF = async () => {
    setDownloadLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/unlock/pdf`, {}, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        withCredentials: true,
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `TenURes_RightToBuy_${user?.ur_id || 'Guide'}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success("PDF downloaded successfully");
    } catch (error) {
      toast.error("Failed to generate PDF");
    } finally {
      setDownloadLoading(false);
    }
  };

  const discountPercent = result?.discount_percent || 0;
  const maxDiscount = result?.max_discount || 0;
  const yearsRequired = 3;
  const yearsTenancy = result?.years_tenancy || 0;
  const yearsToEligibility = Math.max(0, yearsRequired - yearsTenancy);

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 nav-light">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <TenUResLogo size="text-2xl" />
          <Button 
            variant="ghost" 
            className="text-[#111827] hover:text-[#05D38A]"
            onClick={() => navigate('/dashboard')}
            data-testid="back-to-dashboard"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Dashboard
          </Button>
        </div>
      </nav>

      {/* Main Content */}
      <main className="pt-24 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-8"
          >
            <div className="w-16 h-16 rounded-2xl bg-purple-100 flex items-center justify-center mx-auto mb-4">
              <Key className="w-8 h-8 text-purple-600" strokeWidth={1.5} />
            </div>
            <h1 className="text-3xl font-bold text-[#111827] mb-2">
              Unlock: Path to Ownership
            </h1>
            <p className="text-gray-600">
              Track your Right to Buy progress and calculate your discount.
            </p>
          </motion.div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-12 h-12 border-4 border-[#05D38A] border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              {/* Property Info Card */}
              <Card className="bg-white border-gray-200 shadow-lg mb-6">
                <CardContent className="pt-6">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                    <div className="bg-gray-50 rounded-lg p-3">
                      <Calendar className="w-5 h-5 text-gray-400 mx-auto mb-1" />
                      <p className="text-xl font-bold text-[#111827]">{yearsTenancy}</p>
                      <p className="text-gray-500 text-xs">Years Tenancy</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3">
                      <Home className="w-5 h-5 text-gray-400 mx-auto mb-1" />
                      <p className="text-xl font-bold text-[#111827] capitalize">{result?.property_type || '-'}</p>
                      <p className="text-gray-500 text-xs">Property Type</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3">
                      <MapPin className="w-5 h-5 text-gray-400 mx-auto mb-1" />
                      <p className="text-xl font-bold text-[#111827]">{result?.is_london ? 'London' : 'England'}</p>
                      <p className="text-gray-500 text-xs">Location</p>
                    </div>
                    <div className={`rounded-lg p-3 ${result?.eligible ? 'bg-purple-50' : 'bg-gray-50'}`}>
                      <TrendingUp className={`w-5 h-5 mx-auto mb-1 ${result?.eligible ? 'text-purple-600' : 'text-gray-400'}`} />
                      <p className={`text-xl font-bold ${result?.eligible ? 'text-purple-600' : 'text-[#111827]'}`}>
                        {result?.eligible ? 'Yes' : 'No'}
                      </p>
                      <p className="text-gray-500 text-xs">Eligible</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Main Result Card */}
              {result?.eligible ? (
                <Card className="border-2 border-purple-300 bg-purple-50/50 shadow-lg">
                  <CardContent className="pt-6">
                    <div className="text-center mb-6">
                      <h2 className="text-xl font-bold text-[#111827] mb-2">
                        Your Discount Tier
                      </h2>
                      <p className="text-5xl font-bold text-purple-600 mb-2">
                        {discountPercent}%
                      </p>
                      <p className="text-gray-600 text-sm">
                        Current discount based on {yearsTenancy} years tenancy
                      </p>
                    </div>
                    
                    {/* Progress Bar */}
                    <div className="mb-6">
                      <div className="flex justify-between text-sm mb-2">
                        <span className="text-gray-500">Discount Progress</span>
                        <span className="text-purple-600 font-medium">{discountPercent}% of 70% max</span>
                      </div>
                      <div className="progress-bar-track h-4">
                        <div 
                          className="h-full rounded-full bg-gradient-to-r from-purple-500 to-purple-400 transition-all duration-1000"
                          style={{ width: `${(discountPercent / 70) * 100}%` }}
                        />
                      </div>
                      <div className="flex justify-between text-xs text-gray-400 mt-1">
                        <span>0%</span>
                        <span>35% (3 yrs)</span>
                        <span>70% (max)</span>
                      </div>
                    </div>
                    
                    {/* Max Discount Cap */}
                    <div className="bg-white rounded-xl p-4 mb-6 border border-purple-200">
                      <div className="flex justify-between items-center">
                        <div>
                          <p className="text-gray-500 text-sm">Maximum Discount Cap</p>
                          <p className="text-gray-400 text-xs">
                            ({result?.is_london ? 'London' : 'England'} statutory limit)
                          </p>
                        </div>
                        <p className="text-2xl font-bold text-[#111827]">
                          £{maxDiscount.toLocaleString()}
                        </p>
                      </div>
                    </div>
                    
                    {/* Key Info */}
                    <div className="space-y-3 mb-6">
                      <div className="flex items-start gap-3 text-sm">
                        <AlertCircle className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
                        <p className="text-gray-600">
                          <strong className="text-[#111827]">Cost Floor Rule:</strong> Your discount may be limited 
                          if your landlord has spent significant amounts on improvements in the last 15 years.
                        </p>
                      </div>
                      <div className="flex items-start gap-3 text-sm">
                        <AlertCircle className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
                        <p className="text-gray-600">
                          <strong className="text-[#111827]">Exemptions:</strong> Some properties (sheltered housing, 
                          certain rural areas) may be exempt from Right to Buy.
                        </p>
                      </div>
                    </div>
                    
                    <Button
                      onClick={downloadPDF}
                      className="w-full bg-[#05D38A] hover:bg-[#04B577] text-[#111827] font-semibold glow-mint"
                      disabled={downloadLoading}
                      data-testid="download-pdf-btn"
                    >
                      {downloadLoading ? (
                        <div className="w-5 h-5 border-2 border-[#111827] border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <>
                          <Download className="w-4 h-4 mr-2" />
                          Download Path to Ownership PDF
                        </>
                      )}
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <Card className="bg-white border-gray-200 shadow-lg">
                  <CardContent className="pt-6">
                    <div className="text-center">
                      <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
                        <Clock className="w-8 h-8 text-gray-400" />
                      </div>
                      
                      <h2 className="text-xl font-bold text-[#111827] mb-2">
                        Not Yet Eligible
                      </h2>
                      
                      <p className="text-gray-600 mb-6">
                        {result?.reason || "You need at least 3 years tenancy to qualify for Right to Buy."}
                      </p>
                      
                      {yearsToEligibility > 0 && (
                        <div className="bg-gray-50 rounded-xl p-4 mb-6">
                          <p className="text-gray-500 text-sm mb-2">Time until eligibility</p>
                          <p className="text-3xl font-bold text-[#111827]">
                            {yearsToEligibility} year{yearsToEligibility > 1 ? 's' : ''}
                          </p>
                          <p className="text-gray-500 text-sm mt-2">
                            You'll be eligible in {new Date().getFullYear() + yearsToEligibility}
                          </p>
                        </div>
                      )}
                      
                      <p className="text-gray-500 text-sm">
                        Continue living at your property and come back to check your eligibility status.
                      </p>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Disclaimer */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
                className="mt-6 text-center"
              >
                <p className="text-gray-500 text-sm">
                  This is an estimate based on current rules. Final eligibility and valuations are determined by your landlord. 
                  Seek professional advice before proceeding.
                </p>
              </motion.div>
            </motion.div>
          )}
        </div>
      </main>
    </div>
  );
}
